// The undated bank (contracts/studio.md §3–4): curated, generator, Claude and manual puzzles with
// filters (source, category, type, review, verification, sourceId search), paging, and a preview drawer
// with the rendered puzzle, answer, explanation, verification notes and sourceId, plus Re-verify, Edit
// spec, Schedule to date and the review.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router";
import { PuzzleView } from "@lumen/puzzle-render";
import { usePuzzle } from "../../api/queries";
import { Category, PuzzleSource, PuzzleStatus, ReviewState, type VerificationSummary } from "../../api/schemas";
import { useBank, useStudioTypes, useVerify, type BankQuery } from "../../api/studio";
import type { BankListItem } from "../../api/studioSchemas";
import { useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, CategoryTag, Chip, EmptyState, Field, Loading, PageHeader, Select, Skeleton, StatusChip, TextInput, TierDots } from "../../components/ui";
import { KNOWN_TYPES } from "../../lib/catalog";
import { useHotkey } from "../../lib/hotkeys";
import { sourceIdOf, verificationBadge, type BadgeState } from "../../lib/studio";
import pages from "../pages.module.css";
import { AnswerText, BackLink, RoleNote, ReviewActions, SourceChip, StudioError, VerificationChip, VerificationDetails } from "./components";
import { ScheduleDialog, SpecEditorDialog, type ScheduleSubject } from "./dialogs";
import s from "./studio.module.css";

const FILTERS = ["q", "source", "category", "type", "review", "status", "verification", "limit"] as const;
type FilterKey = (typeof FILTERS)[number];

/** The API has no verification filter: the panel applies it to the rows it loaded. */
const VERIFICATION_FILTERS: { value: string; label: string; keep: (b: BadgeState) => boolean }[] = [
  { value: "unique", label: "solver ✓ unique", keep: (b) => b === "unique" },
  { value: "review", label: "needs review", keep: (b) => b === "review" },
  { value: "reviewed", label: "reviewed ✓", keep: (b) => b === "reviewed" },
  { value: "failed", label: "failed", keep: (b) => b === "failed" },
  { value: "unverified", label: "not verified", keep: (b) => b === "unverified" || b === "build" },
];

export function StudioBankPage() {
  const [params, setParams] = useSearchParams();
  const [qDraft, setQDraft] = useState(params.get("q") ?? "");
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const types = useStudioTypes();
  const openId = params.get("id");

  const verification = params.get("verification") ?? "";
  const filters = useMemo(() => {
    const f: BankQuery = {};
    for (const k of ["q", "source", "category", "type", "review", "status"] as const) {
      const v = params.get(k);
      if (v) (f as Record<string, string>)[k] = v;
    }
    // With a verification filter, load the widest page the API allows and filter it here.
    f.limit = verification ? 200 : Number(params.get("limit") ?? 50) || 50;
    return f;
  }, [params, verification]);
  const filterKey = JSON.stringify(filters);
  const [pageFor, setPageFor] = useState(filterKey);
  if (pageFor !== filterKey) {
    setPageFor(filterKey);
    setCursors([null]);
  }
  const cursor = cursors[cursors.length - 1] ?? null;
  const q = useBank({ ...filters, cursor });

  useEffect(() => {
    const t = window.setTimeout(() => {
      if ((params.get("q") ?? "") !== qDraft) update({ q: qDraft.trim() });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);

  function update(patch: Partial<Record<FilterKey | "id", string>>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  }

  const keep = VERIFICATION_FILTERS.find((v) => v.value === verification)?.keep;
  const rows = (q.data?.items ?? []).filter((p) => !keep || keep(verificationBadge(p.verification, p.reviewState, p.source).state));
  const hasFilters = FILTERS.some((k) => k !== "limit" && params.get(k));
  const typeOptions = useMemo(() => {
    const all = new Set<string>([...(types.data?.types.map((t) => t.type) ?? []), ...KNOWN_TYPES]);
    const cat = params.get("category");
    return [...all].filter((t) => !cat || t.startsWith(`${cat}.`)).sort();
  }, [types.data, params]);

  return (
    <>
      <PageHeader
        title="Bank"
        meta={
          <>
            <BackLink to="/studio">Studio</BackLink>
            <span>Undated puzzles: curated, generated and Claude drafts. Nothing here reaches players until it is scheduled.</span>
          </>
        }
        actions={<RoleNote />}
      />

      <div className={pages.filters} data-testid="bank-filters">
        <Field label="Search id / sourceId">
          <TextInput data-search placeholder="pattern-24, gen-…  ( / )" value={qDraft} onChange={(e) => setQDraft(e.target.value)} style={{ width: 200 }} aria-label="Search id or sourceId" />
        </Field>
        <Field label="Source">
          <Select value={params.get("source") ?? ""} onChange={(e) => update({ source: e.target.value })} aria-label="Source">
            <option value="">Any</option>
            {PuzzleSource.options.map((o) => (
              <option key={o} value={o}>
                {o === "ai" ? "ai (Claude)" : o}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Category">
          <Select value={params.get("category") ?? ""} onChange={(e) => update({ category: e.target.value, type: "" })} aria-label="Category">
            <option value="">Any</option>
            {Category.options.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Type">
          <TextInput list="bank-types" placeholder="any type" value={params.get("type") ?? ""} onChange={(e) => update({ type: e.target.value.trim() })} style={{ width: 200 }} aria-label="Type" />
          <datalist id="bank-types">
            {typeOptions.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </Field>
        <Field label="Review">
          <Select value={params.get("review") ?? ""} onChange={(e) => update({ review: e.target.value })} aria-label="Review">
            <option value="">Any</option>
            {ReviewState.options.map((r) => (
              <option key={r} value={r}>
                {r === "none" ? "none (solver)" : r}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Verification">
          <Select value={verification} onChange={(e) => update({ verification: e.target.value })} aria-label="Verification">
            <option value="">Any</option>
            {VERIFICATION_FILTERS.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Status">
          <Select value={params.get("status") ?? ""} onChange={(e) => update({ status: e.target.value })} aria-label="Status">
            <option value="">Any</option>
            {PuzzleStatus.options
              .filter((o) => o !== "published" && o !== "retired")
              .map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
          </Select>
        </Field>
        <div className={pages.filtersEnd}>
          {hasFilters ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setQDraft("");
                setParams(openId ? { id: openId } : {}, { replace: true });
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </div>
      </div>

      {q.isError ? (
        <StudioError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className={tableStyles.wrap} aria-busy={q.isFetching}>
            <table className={tableStyles.table} data-testid="bank">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Source</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Difficulty</th>
                  <th>Status</th>
                  <th>Verification</th>
                </tr>
              </thead>
              <tbody>
                {q.isPending
                  ? Array.from({ length: 10 }, (_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 7 }, (__, j) => (
                          <td key={j}>
                            <Skeleton height={12} width={j === 0 ? 120 : 64} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : rows.map((p) => <BankRow key={p.id} p={p} selected={p.id === openId} onOpen={() => update({ id: p.id })} />)}
              </tbody>
            </table>
            {!q.isPending && rows.length === 0 ? (
              <EmptyState
                title="No bank puzzles match"
                action={
                  hasFilters ? (
                    <Button size="sm" onClick={() => setParams({}, { replace: true })}>
                      Clear filters
                    </Button>
                  ) : undefined
                }
              >
                {hasFilters ? "Try another source, type or review state." : "Seed the curated bank (pnpm seed:curated) or queue a job."}
              </EmptyState>
            ) : null}
          </div>
          <div className={tableStyles.footer}>
            <span className="mono" data-testid="bank-footer">
              Page {cursors.length}
              {q.data ? ` · ${rows.length}${keep ? ` of ${q.data.items.length} loaded (verification is filtered here)` : " rows"}` : ""}
              {q.isFetching && !q.isPending ? " · updating…" : ""}
            </span>
            <div className={tableStyles.pager}>
              {!verification ? (
                <Select aria-label="Rows per page" value={String(filters.limit ?? 50)} onChange={(e) => update({ limit: e.target.value === "50" ? "" : e.target.value })}>
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </Select>
              ) : null}
              <Button size="sm" variant="ghost" disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
                ← Prev
              </Button>
              <Button size="sm" variant="ghost" disabled={!q.data?.nextCursor || q.isPlaceholderData} onClick={() => q.data?.nextCursor && setCursors((c) => [...c, q.data.nextCursor])}>
                Next →
              </Button>
            </div>
          </div>
        </>
      )}

      {openId ? <BankDrawer id={openId} onClose={() => update({ id: "" })} /> : null}
    </>
  );
}

function BankRow({ p, selected, onOpen }: { p: BankListItem; selected: boolean; onOpen: () => void }) {
  const sid = sourceIdOf(p.id, p.source);
  return (
    <tr
      data-clickable
      data-selected={selected || undefined}
      data-id={p.id}
      onClick={(e) => {
        if (!e.currentTarget.contains(e.target as Node) || (e.target as HTMLElement).closest("a,button")) return;
        onOpen();
      }}
    >
      <td>
        <button type="button" className={s.idButton} onClick={onOpen} aria-label={`Preview ${p.id}`}>
          {p.id}
        </button>
        {sid ? <span className={s.idSub}>sourceId {sid}</span> : null}
      </td>
      <td>
        <SourceChip source={p.source} />
      </td>
      <td className={tableStyles.mono}>{p.type}</td>
      <td>
        <CategoryTag category={p.category} />
      </td>
      <td>
        <span className={pages.diffCell}>
          <TierDots tier={p.difficulty.tier} />
          <span className={tableStyles.mono}>{p.difficulty.score.toFixed(2)}</span>
        </span>
      </td>
      <td>
        <StatusChip status={p.status} />
      </td>
      <td>
        <VerificationChip badge={verificationBadge(p.verification, p.reviewState, p.source)} />
      </td>
    </tr>
  );
}

function BankDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = usePuzzle(id);
  const canEdit = useCan("studio.edit");
  const verify = useVerify();
  const [reveal, setReveal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [scheduling, setScheduling] = useState<ScheduleSubject | null>(null);
  useHotkey("r", () => setReveal((v) => !v));
  useHotkey("Escape", () => onClose());

  const p = q.data?.puzzle;
  const summary: VerificationSummary = p?.verification ? { method: p.verification.method, ok: p.verification.ok } : null;
  const badge = p ? verificationBadge(p.verification ?? null, p.reviewState, p.source) : null;
  const inBank = p ? (p.mode ?? "bank") === "bank" : false;

  return createPortal(
    <aside className={s.drawer} aria-label={`Bank puzzle ${id}`} data-testid="bank-drawer">
      <header className={s.drawerHead}>
        <span className={s.drawerTitle}>{id}</span>
        {p?.sourceId ? <Chip tone="teams">{`sourceId ${p.sourceId}`}</Chip> : null}
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close preview">
          ✕
        </Button>
      </header>
      <div className={s.drawerBody}>
        {q.isPending ? (
          <Loading rows={8} />
        ) : q.isError ? (
          <StudioError error={q.error} onRetry={() => void q.refetch()} />
        ) : p ? (
          <>
            <div className={s.chipsRow}>
              <StatusChip status={p.status} />
              <SourceChip source={p.source} />
              {badge ? <VerificationChip badge={badge} /> : null}
              <span className="mono">{p.type}</span>
              <CategoryTag category={p.category} />
              {!inBank ? <Chip tone="info">{`${p.mode} · ${p.date ?? "—"}`}</Chip> : null}
            </div>

            <div className={s.drawerActions}>
              {canEdit ? (
                <>
                  <Button size="sm" variant="secondary" busy={verify.isPending} onClick={() => verify.mutate(p.id)}>
                    Re-verify
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setEditing(true)} disabled={p.status !== "draft"} title={p.status !== "draft" ? "Only drafts can be edited" : undefined}>
                    Edit spec
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!inBank}
                    title={inBank ? undefined : "Already scheduled"}
                    onClick={() => setScheduling({ id: p.id, type: p.type, status: p.status, mode: p.mode ?? "bank", source: p.source, reviewState: p.reviewState, verification: p.verification ?? null })}
                  >
                    Schedule to date
                  </Button>
                  <ReviewActions puzzle={{ id: p.id, status: p.status, verification: summary }} />
                </>
              ) : null}
              <Link to={`/puzzles/${encodeURIComponent(p.id)}`} className={tableStyles.id} style={{ alignSelf: "center", marginLeft: "auto" }}>
                Full page →
              </Link>
            </div>
            {verify.error ? <StudioError error={verify.error} /> : null}
            {verify.data && verify.variables === p.id ? (
              <div data-testid="verify-result" className={s.chipsRow}>
                <span className="monoLabel">Re-verified</span>
                <VerificationChip badge={verificationBadge(verify.data.verification, verify.data.puzzle.reviewState, verify.data.puzzle.source)} />
              </div>
            ) : null}

            <div className={pages.stage}>
              <PuzzleView spec={p.spec} answer={p.answer} reveal={reveal} />
            </div>
            <div className={pages.answerLine} style={{ marginTop: 0 }}>
              <span className="monoLabel">Answer</span>
              {reveal ? <AnswerText answer={p.answer} /> : <span className={pages.hidden}>hidden · press R</span>}
              <Button size="sm" variant={reveal ? "primary" : "ghost"} aria-pressed={reveal} onClick={() => setReveal((v) => !v)} shortcut="R" style={{ marginLeft: "auto" }}>
                {reveal ? "Hide" : "Reveal"}
              </Button>
            </div>
            <section className={s.section}>
              <span className={s.sectionTitle}>Explanation</span>
              {reveal ? <p className={pages.prose} style={{ margin: 0 }} data-testid="explanation">{p.explanation || "—"}</p> : <span className={pages.hidden}>hidden with the answer</span>}
            </section>
            <section className={s.section}>
              <span className={s.sectionTitle}>Verification</span>
              <VerificationDetails v={p.verification} />
            </section>
            <section className={s.section}>
              <span className={s.sectionTitle}>Difficulty</span>
              <span className={pages.diffCell}>
                <TierDots tier={p.spec.difficulty.tier} />
                <span className="mono">{p.spec.difficulty.score.toFixed(2)}</span>
              </span>
            </section>
            {p.history.length ? (
              <section className={s.section}>
                <span className={s.sectionTitle}>History</span>
                <ol className={pages.timeline}>
                  {[...p.history].reverse().map((h, i) => (
                    <li key={`${h.at}-${i}`}>
                      <StatusChip status={h.status} />
                      <span className="mono">{h.by ?? "system"}</span>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}
          </>
        ) : null}
      </div>
      {editing ? <SpecEditorDialog id={id} onClose={() => setEditing(false)} /> : null}
      <ScheduleDialog puzzle={scheduling} onClose={() => setScheduling(null)} />
    </aside>,
    document.body,
  );
}
